/**
 * Luật "một việc chiếm những ngày nào trên lịch" — APM-MYT-b.
 *
 * ## Vì sao nằm ở contracts
 *
 * Lịch cá nhân (web) vẽ thẻ theo luật này, còn lịch đội (API) **đếm** theo nó.
 * Hai bản viết tay ở hai phía sẽ lệch — và lệch theo hướng im lặng: ô ngày của
 * lịch đội ghi "Anna ·3" trong khi mở lịch của Anna ra thì thấy hai thẻ. Một hàm
 * thuần dùng chung thì cả hai chỉ có thể cùng đúng hoặc cùng sai.
 *
 * Mọi ngày ở đây là chuỗi `YYYY-MM-DD` và so bằng thứ tự chuỗi — đúng vì định
 * dạng cố định độ rộng. Không đi qua timezone nào: cột `DATE` là ngày thuần.
 */
const DAY_MS = 86_400_000;
/**
 * Khoảng ngày việc này chiếm, hoặc `null` nếu nó không lên lịch.
 *
 * Có cả hai mốc thì chiếm trọn khoảng giữa — một việc kéo ba tuần phải hiện ở
 * cả ba tuần. Chỉ có một mốc thì chiếm đúng ngày đó. Không mốc nào thì không
 * lên lịch. Mốc đầu **sau** mốc cuối (dữ liệu lỗi) thì cũng không chiếm ngày
 * nào, giữ đúng hành vi cũ của lịch cá nhân.
 */
export function occupiedSpan(item) {
    const start = item.startDate ?? item.dueDate;
    const end = item.dueDate ?? item.startDate;
    if (!start || !end || start > end)
        return null;
    return { start, end };
}
/** Việc này có "chiếm" ngày `day` không. */
export function occupiesDay(item, day) {
    const span = occupiedSpan(item);
    return span !== null && span.start <= day && day <= span.end;
}
/**
 * Những ngày việc này chiếm **trong** `[from, to]`, theo thứ tự.
 *
 * Việc vắt qua mép kỳ chỉ được tính những ngày nằm trong kỳ: lịch tuần không
 * được đếm cả ba tuần của một việc dài vào bảy ô của nó.
 */
export function occupiedDaysWithin(item, from, to) {
    const span = occupiedSpan(item);
    if (span === null)
        return [];
    const first = span.start > from ? span.start : from;
    const last = span.end < to ? span.end : to;
    const days = [];
    for (let ms = isoMs(first); ms <= isoMs(last); ms += DAY_MS)
        days.push(msIso(ms));
    return days;
}
/**
 * Đếm số việc chiếm mỗi (ngày, người, dự án) trong `[from, to]`.
 *
 * Chỉ trả ô có `count > 0`, xếp theo ngày → người → dự án để kết quả ổn định
 * giữa hai lần gọi (và so được bằng `toEqual` trong test).
 */
export function countOccupancy(items, from, to) {
    const counts = new Map();
    for (const item of items) {
        for (const date of occupiedDaysWithin(item, from, to)) {
            const key = `${date}|${item.userId}|${item.projectId}`;
            const current = counts.get(key);
            counts.set(key, {
                date,
                userId: item.userId,
                projectId: item.projectId,
                count: (current?.count ?? 0) + 1,
            });
        }
    }
    return [...counts.values()].sort((a, b) => compare(a.date, b.date) || compare(a.userId, b.userId) || compare(a.projectId, b.projectId));
}
function compare(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
function isoMs(date) {
    return Date.parse(`${date}T00:00:00Z`);
}
function msIso(ms) {
    return new Date(ms).toISOString().slice(0, 10);
}
//# sourceMappingURL=day-occupancy.js.map